/**
 * SHA-256 em JavaScript puro.
 *
 * Existe por um motivo específico: a autorização offline precisa comparar
 * senha, e guardar senha de supervisor em claro no aparelho é convite para
 * quem achar o aparelho perdido — o banco é legível com root. Guardamos o
 * resumo; a comparação continua funcionando igual.
 *
 * React Native não traz `crypto.subtle`, e a alternativa seria uma dependência
 * nativa a mais para 60 linhas de conta. Correção verificada contra os vetores
 * oficiais no teste ao lado.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function paraBytesUtf8(texto: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < texto.length; i++) {
    let ponto = texto.charCodeAt(i);

    // Par substituto: junta os dois meios num único ponto de código.
    if (ponto >= 0xd800 && ponto <= 0xdbff && i + 1 < texto.length) {
      const baixo = texto.charCodeAt(i + 1);
      if (baixo >= 0xdc00 && baixo <= 0xdfff) {
        ponto = (ponto - 0xd800) * 0x400 + (baixo - 0xdc00) + 0x10000;
        i++;
      }
    }

    if (ponto < 0x80) {
      bytes.push(ponto);
    } else if (ponto < 0x800) {
      bytes.push(0xc0 | (ponto >> 6), 0x80 | (ponto & 0x3f));
    } else if (ponto < 0x10000) {
      bytes.push(
        0xe0 | (ponto >> 12),
        0x80 | ((ponto >> 6) & 0x3f),
        0x80 | (ponto & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (ponto >> 18),
        0x80 | ((ponto >> 12) & 0x3f),
        0x80 | ((ponto >> 6) & 0x3f),
        0x80 | (ponto & 0x3f),
      );
    }
  }
  return bytes;
}

const girar = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

export function sha256(mensagem: string): string {
  const bytes = paraBytesUtf8(mensagem);
  const bitsTotais = bytes.length * 8;

  bytes.push(0x80);
  while (bytes.length % 64 !== 56) {
    bytes.push(0);
  }
  // Tamanho em bits, 64 bits big-endian. Acima de 2^32 bits não acontece aqui.
  bytes.push(0, 0, 0, 0);
  bytes.push(
    (bitsTotais >>> 24) & 0xff,
    (bitsTotais >>> 16) & 0xff,
    (bitsTotais >>> 8) & 0xff,
    bitsTotais & 0xff,
  );

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const w = new Uint32Array(64);

  for (let bloco = 0; bloco < bytes.length; bloco += 64) {
    for (let i = 0; i < 16; i++) {
      const j = bloco + i * 4;
      w[i] =
        ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>>
        0;
    }
    for (let i = 16; i < 64; i++) {
      const s0 = girar(w[i - 15], 7) ^ girar(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = girar(w[i - 2], 17) ^ girar(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let i = 0; i < 64; i++) {
      const S1 = girar(e, 6) ^ girar(e, 11) ^ girar(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = girar(a, 2) ^ girar(a, 13) ^ girar(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map(n => n.toString(16).padStart(8, '0'))
    .join('');
}

/**
 * Resumo da senha de um usuário. O id entra na conta para que dois usuários com
 * a mesma senha não fiquem com o mesmo resumo no banco.
 */
export function resumoDeSenha(usuarioId: number | string, senha: string): string {
  return sha256(`tmobi-validador:${usuarioId}:${senha}`);
}
